'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Heart, Copy, CheckCircle, Zap, Server, Shield, Users } from 'lucide-react';

const PIX_KEY = 'lutecontraovicio@gmail.com';

const costs = [
  {
    icon: Server,
    title: 'Servidores & Hospedagem',
    description: 'Infraestrutura para manter a plataforma disponível 24h por dia, 7 dias por semana.',
  },
  {
    icon: Shield,
    title: 'Segurança & Privacidade',
    description: 'Proteção dos seus dados e dos dados de todos os usuários em recuperação.',
  },
  {
    icon: Zap,
    title: 'Ferramentas de Apoio',
    description: 'Recursos de IA, modo crise e ferramentas terapêuticas que ajudam na recuperação.',
  },
  {
    icon: Users,
    title: 'Moderação & Suporte',
    description: 'Voluntários e moderadores que garantem um ambiente seguro para todos.',
  },
];

export default function DonationScreen() {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(PIX_KEY)?.then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <div className="min-h-screen rounded-2xl" style={{ backgroundColor: '#1A1A2E', padding: '2rem 1.5rem' }}>
      <div className="max-w-2xl mx-auto space-y-8 pb-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mx-auto"
            style={{ backgroundColor: 'rgba(124,158,135,0.2)', border: '2px solid rgba(124,158,135,0.4)' }}
          >
            <Heart className="w-8 h-8" style={{ color: '#7C9E87' }} />
          </div>
          <h1
            className="font-serif"
            style={{ fontSize: 'clamp(1.8rem, 5vw, 2.6rem)', color: '#F8F7F4' }}
          >
            Apoie a Página
          </h1>
          <p className="text-base leading-relaxed max-w-lg mx-auto" style={{ color: 'rgba(248,247,244,0.7)' }}>
            O <strong style={{ color: '#F8F7F4' }}>1 Dia de Cada Vez</strong> é uma plataforma 100% gratuita para quem luta contra o vício em apostas.
            Para continuar funcionando e crescendo, precisamos do seu apoio.
          </p>
        </div>

        {/* Platform info note */}
        <div
          className="rounded-2xl p-5"
          style={{
            backgroundColor: 'rgba(124,158,135,0.08)',
            border: '1px solid rgba(124,158,135,0.2)',
          }}
        >
          <p className="text-sm leading-relaxed text-center" style={{ color: 'rgba(248,247,244,0.8)' }}>
            A plataforma <strong style={{ color: '#F8F7F4' }}>1 dia de cada vez</strong> é um projeto independente mantido por seu fundador. As doações recebidas são utilizadas exclusivamente para manutenção e desenvolvimento da plataforma.
          </p>
        </div>

        {/* Why we need donations */}
        <div
          className="rounded-2xl p-6"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#C4A882' }}>
            Por que precisamos do seu apoio?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {costs?.map((item) => {
              const ItemIcon = item?.icon;
              return (
                <div
                  key={item?.title}
                  className="flex gap-3 p-3 rounded-xl"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                >
                  <div
                    className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(124,158,135,0.2)' }}
                  >
                    <ItemIcon className="w-5 h-5" style={{ color: '#7C9E87' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#F8F7F4' }}>{item?.title}</p>
                    <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'rgba(248,247,244,0.5)' }}>
                      {item?.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PIX Donation Card */}
        <div
          className="rounded-2xl p-6 space-y-5"
          style={{
            background: 'linear-gradient(135deg, rgba(124,158,135,0.25) 0%, rgba(124,158,135,0.12) 100%)',
            border: '1px solid rgba(124,158,135,0.35)',
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: 'rgba(124,158,135,0.3)' }}
            >
              <Heart className="w-4 h-4" style={{ color: '#7C9E87' }} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#F8F7F4' }}>Doe via PIX</h2>
          </div>

          <p className="text-sm leading-relaxed" style={{ color: 'rgba(248,247,244,0.7)' }}>
            Qualquer valor faz diferença. Copie a chave PIX abaixo e doe pelo aplicativo do seu banco.
          </p>

          {/* PIX Key */}
          <div
            className="rounded-xl p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: '1px solid rgba(124,158,135,0.3)' }}
          >
            <p
              className="text-xs font-medium mb-1 uppercase tracking-wide"
              style={{ color: 'rgba(248,247,244,0.5)' }}
            >
              Chave PIX (e-mail)
            </p>
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-sm break-all" style={{ color: '#F8F7F4' }}>{PIX_KEY}</span>
              <button
                onClick={handleCopy}
                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: copied ? 'rgba(124,158,135,0.4)' : 'rgba(124,158,135,0.25)',
                  border: '1px solid rgba(124,158,135,0.5)',
                  color: '#F8F7F4',
                }}
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-3.5 h-3.5" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copiar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'rgba(248,247,244,0.5)' }}>
              Como doar:
            </p>
            <ol className="space-y-1.5">
              {[
                'Abra o aplicativo do seu banco',
                'Acesse a área de PIX',
                'Escolha "Pagar" ou "Transferir"',
                'Cole a chave PIX acima',
                'Digite o valor que desejar e confirme',
              ]?.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'rgba(248,247,244,0.75)' }}>
                  <span
                    className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                    style={{ backgroundColor: 'rgba(124,158,135,0.3)', color: '#7C9E87' }}
                  >
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Vakinha Link */}
        <div
          className="rounded-2xl p-6"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(251,146,60,0.2)' }}
            >
              <span className="text-xl">🐄</span>
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#C4A882' }}>Doe pela Vakinha</h2>
          </div>
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(248,247,244,0.7)' }}>
            Prefere usar a plataforma Vakinha? Acesse nossa campanha e contribua com qualquer valor para ajudar a manter o projeto vivo.
          </p>
          <a
            href="https://www.vakinha.com.br/vaquinha/projeto-social-contra-o-vicio-em-apostas-bets-e-cassinos"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#f97316', color: '#fff' }}
          >
            <span>Acessar Vakinha</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>

        {/* Thank you note */}
        <div
          className="text-center rounded-2xl p-5"
          style={{
            backgroundColor: 'rgba(196,168,130,0.1)',
            border: '1px solid rgba(196,168,130,0.25)',
          }}
        >
          <p className="text-sm font-medium" style={{ color: '#C4A882' }}>
            💛 Cada doação, por menor que seja, ajuda a manter essa plataforma viva e gratuita para quem mais precisa.
          </p>
          <p className="text-xs mt-1" style={{ color: 'rgba(196,168,130,0.7)' }}>
            Obrigado por fazer parte dessa missão de recuperação.
          </p>
        </div>
      </div>
    </div>
  );
}
